import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, List, Tag, Badge, Empty, Typography, Button, Tabs } from 'antd';
import { ScheduleOutlined, DownOutlined, RightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAgendaItems } from '../../api/agendaItems';
import { urgencyMeta } from '../../constants/agendaUrgency';

const { Text } = Typography;
const PAGE_SIZE = 5;

const AgendaPendingPanel = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [aktifSekme, setAktifSekme] = useState('bugun');

  const [bugunItems, setBugunItems]     = useState([]);
  const [bugunTotal, setBugunTotal]     = useState(0);
  const [gecmisItems, setGecmisItems]   = useState([]);
  const [gecmisTotal, setGecmisTotal]   = useState(0);
  const [loading, setLoading]           = useState(true);

  // İKİ LİSTE DE AÇILIŞTA ÇEKİLİYOR, sekme açılınca değil.
  //
  // Kart varsayılan olarak KAPALI ve sekme rozetlerindeki sayılar tam da
  // kartı açmaya değer olup olmadığını söylüyor. Gecikmiş listeyi sekmeye
  // tıklandığında çekseydik, "3 iş gecikmiş" bilgisi ancak kullanıcı zaten
  // oraya baktıktan sonra görünürdü — yani hiç görünmezdi.
  useEffect(() => {
    const bugun = dayjs().format('YYYY-MM-DD');

    Promise.all([
      getAgendaItems({ status: 'pending', reminder_date: bugun, per_page: PAGE_SIZE }),
      getAgendaItems({ status: 'pending', reminder_date_before: bugun, per_page: PAGE_SIZE }),
    ])
      .then(([bugunRes, gecmisRes]) => {
        setBugunItems(bugunRes.data.data);
        setBugunTotal(bugunRes.data.total);
        setGecmisItems(gecmisRes.data.data);
        setGecmisTotal(gecmisRes.data.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const satir = (item, gecikmis) => {
    const meta = urgencyMeta(item.urgency);
    const gecikmeGun = item.reminder_date
      ? dayjs().startOf('day').diff(dayjs(item.reminder_date).startOf('day'), 'day')
      : 0;

    return (
      <List.Item>
        <div className="w-full">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <Tag color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>
            {item.reminder_date && (
              <Tag icon={<ClockCircleOutlined />} color="red" style={{ margin: 0 }}>
                {dayjs(item.reminder_date).format('DD.MM.YYYY')}
              </Tag>
            )}
            {/* Kaç gün geciktiği, tarihin kendisinden daha hızlı okunuyor —
                "12.08.2026" görüp kafadan gün saymak gerekmiyor. */}
            {gecikmis && gecikmeGun > 0 && (
              <Tag color="volcano" style={{ margin: 0 }}>
                {gecikmeGun} gün gecikti
              </Tag>
            )}
            <Text type="secondary" className="text-xs">
              {dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
            </Text>
          </div>
          <div className="text-sm text-gray-700 mt-0.5" style={{ whiteSpace: 'pre-wrap' }}>
            {item.content}
          </div>
        </div>
      </List.Item>
    );
  };

  const liste = (items, gecikmis, bosMetin) => (
    <List
      size="small"
      loading={loading}
      dataSource={items}
      locale={{ emptyText: <Empty description={bosMetin} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      renderItem={(item) => satir(item, gecikmis)}
    />
  );

  const toplam = bugunTotal + gecmisTotal;

  return (
    <Card
      size="small"
      title={
        <span
          className="cursor-pointer select-none flex items-center gap-2"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <RightOutlined style={{ fontSize: 11, color: '#9ca3af' }} /> : <DownOutlined style={{ fontSize: 11, color: '#9ca3af' }} />}
          <ScheduleOutlined style={{ color: '#1677ff' }} />
          Ajanda — Hatırlatmalar
          {/* Gecikmiş iş varsa rozet KIRMIZI: bugünkü işi zaten bugün
              görüyorsun, dikkat çekmesi gereken unutulmuş olan. */}
          <Badge
            count={toplam}
            style={{ backgroundColor: gecmisTotal > 0 ? '#cf1322' : (toplam > 0 ? '#faad14' : '#d9d9d9') }}
          />
        </span>
      }
      style={{ marginBottom: 16 }}
    >
      {!collapsed && (
        <>
          <Tabs
            size="small"
            activeKey={aktifSekme}
            onChange={setAktifSekme}
            items={[
              {
                key: 'bugun',
                label: <span>Bugün <Badge count={bugunTotal} size="small" style={{ backgroundColor: bugunTotal > 0 ? '#faad14' : '#d9d9d9' }} /></span>,
                children: liste(bugunItems, false, 'Bugün için hatırlatma yok.'),
              },
              {
                key: 'gecmis',
                label: <span>Geçmiş <Badge count={gecmisTotal} size="small" style={{ backgroundColor: gecmisTotal > 0 ? '#cf1322' : '#d9d9d9' }} /></span>,
                children: liste(gecmisItems, true, 'Tarihi geçmiş açık iş yok.'),
              },
            ]}
          />
          {/* AÇIK SEKMEYİ TAŞIYOR. Eskiden filtresiz /agenda açılıyordu:
              kullanıcı "Geçmiş" sekmesinde 10+ gecikmiş iş görüp tıklıyor,
              karşısına tüm görevlerin karışık listesi çıkıyor ve gecikenleri
              ayıklayamıyordu (Eren Bey bildirdi, 12.10.2026). Ajanda bu iki
              parametreyi açılışta okuyor. */}
          <div className="text-center mt-2">
            <Button
              type="link"
              size="small"
              onClick={() => navigate(
                `/agenda?status=pending&hatirlatma=${aktifSekme === 'gecmis' ? 'overdue' : 'today'}`,
              )}
            >
              {aktifSekme === 'gecmis' ? 'Tarihi geçenlerin tümü' : 'Bugünkülerin tümü'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default AgendaPendingPanel;
